/* Uklidov theme: core scripts */
(function () {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};

  /* ---------- helpers ---------- */
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function jsonConfig(body) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    };
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function announce(message) {
    const el = document.getElementById('a11y-live');
    if (el) el.textContent = message;
  }

  window.ukl = { debounce, jsonConfig, parseHTML, announce };

  /* ---------- section load animations ---------- */
  function initAnimations(root = document) {
    if (!document.body.classList.contains('animations-enabled')) return;
    const items = root.querySelectorAll('.animate-item:not(.is-visible)');
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -40px 0px', threshold: 0.05 }
    );
    items.forEach((el) => io.observe(el));
  }

  document.addEventListener('DOMContentLoaded', () => initAnimations());
  document.addEventListener('shopify:section:load', (e) => initAnimations(e.target));

  /* ---------- sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky;
      this.section = this.closest('.shopify-section');
      this.lastY = 0;
      this.setHeight();
      window.addEventListener('resize', debounce(() => this.setHeight(), 150));
      if (this.mode === 'none') return;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
    setHeight() {
      document.documentElement.style.setProperty('--header-height', this.offsetHeight + 'px');
    }
    onScroll() {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 40);
      if (this.mode === 'on-scroll-up' && this.section) {
        const hide = y > this.offsetHeight && y > this.lastY;
        this.section.classList.toggle('header-hidden', hide);
      }
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- mobile menu drawer ---------- */
  class HeaderDrawer extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.details.querySelector('summary');
      this.summary.addEventListener('click', (e) => {
        e.preventDefault();
        this.details.open ? this.close() : this.open();
      });
      this.querySelectorAll('[data-close-drawer]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.addEventListener('keyup', (e) => {
        if (e.code === 'Escape') this.close();
      });
    }
    open() {
      this.details.open = true;
      requestAnimationFrame(() => this.details.classList.add('menu-opening'));
      document.body.classList.add('overflow-hidden');
      this.summary.setAttribute('aria-expanded', 'true');
    }
    close() {
      this.details.classList.remove('menu-opening');
      document.body.classList.remove('overflow-hidden');
      this.summary.setAttribute('aria-expanded', 'false');
      setTimeout(() => (this.details.open = false), 300);
    }
  }
  customElements.define('header-drawer', HeaderDrawer);

  /* ---------- desktop dropdowns ---------- */
  class DetailsDisclosure extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.details.addEventListener('toggle', () => {
        if (this.details.open) {
          document.querySelectorAll('details-disclosure details[open]').forEach((d) => {
            if (d !== this.details) d.open = false;
          });
        }
      });
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.details.open = false;
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.details.open = false;
      });
    }
  }
  customElements.define('details-disclosure', DetailsDisclosure);

  /* ---------- quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button[name="plus"], button[name="minus"]').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const prev = this.input.value;
          if (btn.name === 'plus') this.input.stepUp();
          else this.input.stepDown();
          if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- generic slider ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('[data-slider]');
      if (!this.slider) return;
      this.prevs = this.querySelectorAll('[data-slider-prev]');
      this.nexts = this.querySelectorAll('[data-slider-next]');
      this.dots = this.querySelector('[data-slider-dots]');
      this.controls = this.querySelector('[data-slider-controls]');
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = parseInt(this.dataset.speed || '5', 10) * 1000;
      this.prevs.forEach((b) => b.addEventListener('click', () => this.go(-1)));
      this.nexts.forEach((b) => b.addEventListener('click', () => this.go(1)));
      this.slider.addEventListener('scroll', debounce(() => this.update(), 60), { passive: true });
      this.ro = new ResizeObserver(() => this.build());
      this.ro.observe(this.slider);
      this.build();
      if (this.autoplay) this.startAutoplay();
    }
    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
      clearInterval(this.timer);
    }
    get slides() {
      return Array.from(this.slider.children).filter((el) => el.offsetParent !== null || el.offsetWidth);
    }
    get perView() {
      const first = this.slides[0];
      if (!first) return 1;
      return Math.max(1, Math.round(this.slider.clientWidth / first.offsetWidth));
    }
    get pages() {
      const scrollable = this.slider.scrollWidth - this.slider.clientWidth > 4;
      if (!scrollable) return 1;
      return Math.max(1, this.slides.length - this.perView + 1);
    }
    build() {
      const pages = this.pages;
      this.classList.toggle('is-static', pages <= 1);
      if (this.dots) {
        this.dots.innerHTML = '';
        for (let i = 0; i < pages; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'slider-dot';
          b.setAttribute('aria-label', String(i + 1));
          b.addEventListener('click', () => this.goTo(i));
          this.dots.appendChild(b);
        }
      }
      this.update();
    }
    current() {
      const first = this.slides[0];
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(this.slider).columnGap) || 0;
      return Math.round(this.slider.scrollLeft / (first.offsetWidth + gap));
    }
    goTo(i) {
      const slide = this.slides[i];
      if (!slide) return;
      this.slider.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: 'smooth' });
    }
    go(dir) {
      let i = this.current() + dir;
      if (i < 0) i = this.pages - 1;
      if (i > this.pages - 1) i = 0;
      this.goTo(i);
    }
    update() {
      const i = Math.min(this.current(), this.pages - 1);
      if (this.dots) {
        Array.from(this.dots.children).forEach((d, idx) => d.setAttribute('aria-current', idx === i ? 'true' : 'false'));
      }
    }
    startAutoplay() {
      this.timer = setInterval(() => this.go(1), this.speed);
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => {
        clearInterval(this.timer);
        this.timer = setInterval(() => this.go(1), this.speed);
      });
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------- comparison (before/after) slider ---------- */
  class ComparisonSlider extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('input[type="range"]');
      if (!this.range) return;
      const set = () => this.style.setProperty('--pos', this.range.value + '%');
      this.range.addEventListener('input', set);
      set();
    }
  }
  customElements.define('comparison-slider', ComparisonSlider);

  /* ---------- scroll to top ---------- */
  class ScrollTop extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('button');
      this.offset = parseInt(this.dataset.offset || '400', 10);
      const onScroll = () => this.btn.classList.toggle('is-visible', window.scrollY > this.offset);
      window.addEventListener('scroll', onScroll, { passive: true });
      this.btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
      onScroll();
    }
  }
  customElements.define('scroll-top', ScrollTop);

  /* ---------- misc delegated behaviours ---------- */
  document.addEventListener('change', (e) => {
    const el = e.target;
    if (el.matches && el.matches('[data-autosubmit]') && el.form) el.form.submit();
  });

  document.addEventListener('click', (e) => {
    const share = e.target.closest('[data-share-url]');
    if (share) {
      const url = share.dataset.shareUrl;
      if (navigator.share) navigator.share({ url, title: share.dataset.shareTitle }).catch(() => {});
      else if (navigator.clipboard) navigator.clipboard.writeText(url);
    }
  });
})();

/* product recommendations */
(function () {
  'use strict';
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.children.length) return;
      const io = new IntersectionObserver((entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        fetch(this.dataset.url)
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('product-recommendations');
            if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
            this.querySelectorAll('.animate-item').forEach((el) => el.classList.add('is-visible'));
          })
          .catch(() => {});
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);
})();

/* focus search field when the header search opens */
document.addEventListener('toggle', (e) => {
  const d = e.target;
  if (d.classList && d.classList.contains('header__search') && d.open) {
    const input = d.querySelector('input[type="search"]');
    if (input) setTimeout(() => input.focus(), 30);
  }
}, true);

/* Cart drawer, cart API */
(function () {
  'use strict';
  const { jsonConfig, parseHTML, announce, debounce } = window.ukl;
  const routes = (window.theme && window.theme.routes) || {};
  const strings = (window.theme && window.theme.strings) || {};
  const ROOT = (routes.root || '/').replace(/\/?$/, '/');

  function sectionsToRender() {
    const list = ['cart-icon-bubble'];
    if (document.querySelector('cart-drawer')) list.push('cart-drawer');
    return list;
  }

  function updateBubble(sections) {
    if (!sections || !sections['cart-icon-bubble']) return;
    const doc = parseHTML(sections['cart-icon-bubble']);
    const fresh = doc.querySelector('.cart-count-bubble');
    document.querySelectorAll('#cart-icon-bubble .cart-count-bubble').forEach((old) => {
      if (fresh) old.replaceWith(fresh.cloneNode(true));
    });
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-close]')) {
          e.preventDefault();
          this.close();
        }
        const remove = e.target.closest('[data-remove-line]');
        if (remove) this.updateLine(parseInt(remove.dataset.removeLine, 10), 0);
      });
      this.addEventListener('change', (e) => {
        const input = e.target.closest('.cart-quantity__input');
        if (input) this.onQtyChange(input);
      });
      document.addEventListener('keyup', (e) => {
        if (e.code === 'Escape' && this.classList.contains('is-open')) this.close();
      });
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-cart-toggle]');
        if (toggle) {
          e.preventDefault();
          this.open();
        }
      });
      if (this.hasAttribute('data-test-open')) this.open();
      // external scripts (e.g. custom upsell blocks) can ask for a full refresh
      document.addEventListener('cart:refresh', () => this.refresh());
      document.addEventListener('cart:open', (e) => {
        if (e.target !== this) this.open();
      });
    }
    animateProgress() {
      const el = this.querySelector('.cart-progress');
      const fill = el && el.querySelector('.cart-progress__fill');
      if (!fill) return;
      const target = parseFloat(el.dataset.progress || '0');
      const from = typeof this.lastProgress === 'number' ? this.lastProgress : 0;
      this.lastProgress = target;
      if (from === target) return;
      fill.style.transition = 'none';
      fill.style.width = from + '%';
      fill.offsetWidth; // reflow
      fill.style.transition = '';
      requestAnimationFrame(() => (fill.style.width = target + '%'));
    }
    open() {
      if (!this.classList.contains('is-open')) {
        this.lastProgress = 0;
        setTimeout(() => this.animateProgress(), 150);
      }
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      const panel = this.querySelector('.cart-drawer__panel');
      if (panel) setTimeout(() => panel.focus({ preventScroll: true }), 50);
      this.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    }
    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
    }
    onQtyChange(input) {
      clearTimeout(this.qtyTimer);
      this.qtyTimer = setTimeout(() => {
        const qty = Math.max(0, parseInt(input.value, 10) || 0);
        this.updateLine(parseInt(input.dataset.line, 10), qty);
      }, 300);
    }
    setLoading(on) {
      this.classList.toggle('is-updating', on);
    }
    updateLine(line, quantity) {
      this.setLoading(true);
      fetch(routes.cartChange + '.js', jsonConfig({ line, quantity, sections: sectionsToRender(), sections_url: window.location.pathname }))
        .then((r) => r.json())
        .then((state) => {
          if (state.status) {
            alert(state.description || strings.cartError);
            return this.refresh();
          }
          this.renderContents(state);
        })
        .catch(() => alert(strings.cartError))
        .finally(() => this.setLoading(false));
    }
    refresh() {
      return fetch(`${ROOT}?sections=${sectionsToRender().join(',')}`)
        .then((r) => r.json())
        .then((sections) => this.renderContents({ sections }));
    }
    renderContents(state) {
      const sections = state.sections || {};
      if (sections['cart-drawer']) {
        const doc = parseHTML(sections['cart-drawer']);
        const fresh = doc.querySelector('.cart-drawer__inner');
        const current = this.querySelector('.cart-drawer__inner');
        const freshDrawer = doc.querySelector('cart-drawer');
        if (fresh && current) {
          const body = current.querySelector('.cart-drawer__body');
          const scroll = body ? body.scrollTop : 0;
          current.replaceWith(fresh);
          const newBody = this.querySelector('.cart-drawer__body');
          if (newBody) newBody.scrollTop = scroll;
        }
        if (freshDrawer) this.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
      }
      updateBubble(sections);
      this.animateProgress();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: state }));
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* countdown timer (persists across page loads in this session) */
  class CartTimer extends HTMLElement {
    connectedCallback() {
      this.duration = parseInt(this.dataset.duration || '300', 10);
      this.valueEl = this.querySelector('[data-timer-value]');
      const KEY = 'ukl-cart-timer-end';
      let end = 0;
      try { end = parseInt(sessionStorage.getItem(KEY) || '0', 10); } catch (e) { end = 0; }
      if (!end || end < Date.now()) {
        end = Date.now() + this.duration * 1000;
        try { sessionStorage.setItem(KEY, String(end)); } catch (e) { /* ignore */ }
      }
      this.end = end;
      this.tick = this.tick.bind(this);
      this.tick();
      this.interval = setInterval(this.tick, 1000);
    }
    disconnectedCallback() {
      clearInterval(this.interval);
    }
    tick() {
      let left = Math.round((this.end - Date.now()) / 1000);
      if (left <= 0) {
        this.end = Date.now() + this.duration * 1000;
        try { sessionStorage.setItem('ukl-cart-timer-end', String(this.end)); } catch (e) { /* ignore */ }
        left = this.duration;
      }
      const m = Math.floor(left / 60);
      const s = String(left % 60).padStart(2, '0');
      if (this.valueEl) this.valueEl.textContent = `${m}:${s}`;
    }
  }
  customElements.define('cart-timer', CartTimer);

  /* discount codes in drawer */
  class CartDiscount extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="discount"]');
      this.error = this.querySelector('.cart-discount__error');
      this.btn = this.querySelector('.cart-discount__btn');
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.apply();
      });
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-remove-code]');
        if (rm) this.remove(rm.dataset.removeCode);
      });
    }
    existing() {
      return Array.from(this.querySelectorAll('[data-remove-code]')).map((el) => el.dataset.removeCode);
    }
    showError(msg) {
      this.error.textContent = msg;
      this.error.hidden = !msg;
    }
    send(codes) {
      const drawer = document.querySelector('cart-drawer');
      this.btn.classList.add('is-loading');
      return fetch(routes.cartUpdate + '.js', jsonConfig({ discount: codes.join(','), sections: sectionsToRender(), sections_url: window.location.pathname }))
        .then((r) => r.json())
        .then((state) => {
          if (drawer && state.sections) drawer.renderContents(state);
          return state;
        })
        .finally(() => this.btn && this.btn.classList.remove('is-loading'));
    }
    apply() {
      const code = this.input.value.trim();
      if (!code) return this.showError(this.dataset.error);
      this.showError('');
      const codes = this.existing().filter((c) => c.toLowerCase() !== code.toLowerCase()).concat(code);
      this.send(codes).then((state) => {
        const applied = (state.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
        if (!applied || !applied.applicable) {
          const fresh = document.querySelector('cart-discount');
          if (fresh) fresh.showError(strings.discountError || 'Invalid code');
        }
      });
    }
    remove(code) {
      this.send(this.existing().filter((c) => c !== code));
    }
  }
  customElements.define('cart-discount', CartDiscount);

  /* cart page */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', debounce((e) => {
        const input = e.target.closest('.cart-quantity__input');
        if (input) this.update(parseInt(input.dataset.line, 10), Math.max(0, parseInt(input.value, 10) || 0));
      }, 300));
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-remove-line]');
        if (rm) {
          e.preventDefault();
          this.update(parseInt(rm.dataset.removeLine, 10), 0);
        }
      });
    }
    update(line, quantity) {
      this.classList.add('is-updating');
      fetch(routes.cartChange + '.js', jsonConfig({ line, quantity }))
        .then(() => window.location.reload())
        .catch(() => alert(strings.cartError));
    }
  }
  customElements.define('cart-items', CartItems);

  window.ukl.sectionsToRender = sectionsToRender;
  window.ukl.updateBubble = updateBubble;
})();

/* Product page: form, variants, gallery, sticky bar */
(function () {
  'use strict';
  const { parseHTML, announce } = window.ukl;
  const routes = (window.theme && window.theme.routes) || {};
  const strings = (window.theme && window.theme.strings) || {};

  /* ---------- add to cart ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitBtn = this.form.querySelector('[type="submit"]');
      this.error = this.querySelector('.product-form__error');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
    onSubmit(e) {
      e.preventDefault();
      if (!this.submitBtn || this.submitBtn.hasAttribute('disabled') || this.submitBtn.classList.contains('is-loading')) return;
      const skipCart = this.dataset.skipCart === 'true';
      const drawer = document.querySelector('cart-drawer');
      this.setLoading(true);
      this.showError('');
      const formData = new FormData(this.form);
      if (!formData.get('quantity')) formData.set('quantity', '1');
      let items = null;
      try {
        items = this.bundleItems();
        const extras = this.upsellItems();
        if (extras.length) {
          if (!items) {
            const props = {};
            for (const [k, v] of formData.entries()) {
              const m = k.match(/^properties\[(.+)\]$/);
              if (m && v !== '') props[m[1]] = v;
            }
            const main = { id: parseInt(formData.get('id'), 10), quantity: parseInt(formData.get('quantity'), 10) || 1 };
            if (Object.keys(props).length) main.properties = props;
            if (formData.get('selling_plan')) main.selling_plan = formData.get('selling_plan');
            items = [main];
          }
          items = items.concat(extras);
        }
      } catch (err) {
        this.setLoading(false);
        return;
      }
      let request;
      if (items) {
        const body = { items };
        if (!skipCart && drawer) {
          body.sections = window.ukl.sectionsToRender();
          body.sections_url = window.location.pathname;
        }
        request = fetch(routes.cartAdd + '.js', window.ukl.jsonConfig(body));
      } else {
        if (!skipCart && drawer) {
          formData.append('sections', window.ukl.sectionsToRender().join(','));
          formData.append('sections_url', window.location.pathname);
        }
        request = fetch(routes.cartAdd + '.js', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
      }
      request
        .then((r) => r.json())
        .then((res) => {
          if (res.status) {
            this.showError(res.description || res.message || strings.cartError);
            return;
          }
          if (skipCart) {
            window.location.href = '/checkout';
            return;
          }
          if (!drawer || (window.theme && window.theme.cartType === 'page')) {
            window.location.href = routes.cart;
            return;
          }
          drawer.renderContents(res);
          drawer.open();
          announce(strings.addToCart);
        })
        .catch(() => this.showError(strings.cartError))
        .finally(() => this.setLoading(false));
    }
    /* per-item variant choices inside the selected quantity break, if any */
    upsellItems() {
      const scope = this.closest('.shopify-section') || document;
      return Array.from(scope.querySelectorAll('product-upsell.is-selected'))
        .map((u) => u.variantId())
        .filter(Boolean)
        .map((id) => ({ id, quantity: 1 }));
    }
    bundleItems() {
      const checked = document.querySelector(`input[name="quantity"][form="${this.form.getAttribute('id')}"]:checked`);
      if (!checked) return null;
      const label = document.querySelector(`label[for="${checked.id}"]`);
      const wrap = label && label.querySelector('[data-qb-variants]');
      const json = checked.closest('quantity-breaks') && checked.closest('quantity-breaks').querySelector('[data-qb-variants-json]');
      if (!wrap || !json) return null;
      const variants = JSON.parse(json.textContent);
      const counts = new Map();
      for (const row of wrap.querySelectorAll('[data-qb-row]')) {
        const opts = Array.from(row.querySelectorAll('[data-qb-option]')).map((sel) => sel.value);
        const v = variants.find((x) => x.options.every((o, i) => o === opts[i]));
        if (!v || !v.available) {
          this.showError(window.theme.strings.unavailable + ': ' + opts.join(' / '));
          throw new Error('unavailable');
        }
        counts.set(v.id, (counts.get(v.id) || 0) + 1);
      }
      return Array.from(counts, ([id, quantity]) => ({ id, quantity }));
    }
    setLoading(on) {
      [this.submitBtn, document.querySelector('[data-sticky-submit]')].forEach((b) => b && b.classList.toggle('is-loading', on));
    }
    showError(msg) {
      if (!this.error) return;
      this.error.textContent = msg;
      this.error.hidden = !msg;
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- upsell products (added together with the main product) ---------- */
  class ProductUpsell extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('[data-upsell-variant]');
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-upsell-toggle]');
        if (!t || e.target.closest('select, a')) return;
        e.preventDefault();
        if (this.dataset.style === 'add_button') this.addNow();
        else this.setSelected(!this.classList.contains('is-selected'));
      });
      if (this.select) this.select.addEventListener('change', () => this.updatePrice());
      if (this.dataset.selected === 'true') this.setSelected(true);
    }
    setSelected(on) {
      this.classList.toggle('is-selected', on);
      this.querySelectorAll('[aria-pressed]').forEach((el) => el.setAttribute('aria-pressed', on ? 'true' : 'false'));
    }
    variantId() {
      const id = this.select ? this.select.value : this.dataset.id;
      return parseInt(id, 10) || null;
    }
    updatePrice() {
      const opt = this.select && this.select.selectedOptions[0];
      if (!opt) return;
      const price = this.querySelector('[data-upsell-price]');
      const compare = this.querySelector('[data-upsell-compare]');
      if (price && opt.dataset.price) price.innerHTML = opt.dataset.price;
      if (compare) {
        compare.innerHTML = opt.dataset.compare || '';
        compare.hidden = !opt.dataset.compare;
      }
      this.classList.toggle('is-unavailable', opt.disabled);
    }
    addNow() {
      const id = this.variantId();
      const drawer = document.querySelector('cart-drawer');
      if (!id) return;
      const btn = this.querySelector('[data-upsell-toggle]');
      btn && btn.classList.add('is-loading');
      const body = { items: [{ id, quantity: 1 }] };
      if (drawer) {
        body.sections = window.ukl.sectionsToRender();
        body.sections_url = window.location.pathname;
      }
      fetch(routes.cartAdd + '.js', window.ukl.jsonConfig(body))
        .then((r) => r.json())
        .then((res) => {
          if (res.status) return alert(res.description || strings.cartError);
          if (drawer && !(window.theme && window.theme.cartType === 'page')) {
            drawer.renderContents(res);
            drawer.open();
          } else window.location.href = routes.cart;
        })
        .finally(() => btn && btn.classList.remove('is-loading'));
    }
  }
  customElements.define('product-upsell', ProductUpsell);

  /* ---------- variant selection ---------- */
  class VariantSelects extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.sectionId = this.dataset.section;
      this.url = this.dataset.url;
      this.addEventListener('change', () => this.onChange());
      this.markUnavailable();
    }
    selectedOptions() {
      return Array.from(this.querySelectorAll('[data-option-index]')).map((wrap) => {
        const select = wrap.querySelector('select');
        if (select) return select.value;
        const checked = wrap.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }
    findVariant(options) {
      return this.variants.find((v) => v.options.every((o, i) => o === options[i]));
    }
    selectVariantById(id) {
      const v = this.variants.find((x) => String(x.id) === String(id));
      if (!v) return;
      this.querySelectorAll('[data-option-index]').forEach((wrap, i) => {
        const select = wrap.querySelector('select');
        if (select) select.value = v.options[i];
        else wrap.querySelectorAll('input').forEach((inp) => (inp.checked = inp.value === v.options[i]));
      });
      this.onChange();
    }
    markUnavailable() {
      const selected = this.selectedOptions();
      this.querySelectorAll('[data-option-index]').forEach((wrap, idx) => {
        wrap.querySelectorAll('input').forEach((inp) => {
          const opts = selected.slice();
          opts[idx] = inp.value;
          const v = this.findVariant(opts);
          inp.classList.toggle('is-unavailable', !v || !v.available);
        });
        wrap.querySelectorAll('[data-selected-value]').forEach((el) => (el.textContent = selected[idx] || ''));
      });
    }
    onChange() {
      const options = this.selectedOptions();
      const variant = this.findVariant(options);
      this.markUnavailable();
      const section = document.getElementById('MainProduct-' + this.sectionId) || document;
      const idInputs = section.querySelectorAll('.product-variant-id');
      const submit = section.querySelector('.product-form__submit');
      if (!variant) {
        if (submit) {
          submit.setAttribute('disabled', '');
          const t = submit.querySelector('.product-form__submit-text');
          if (t) t.textContent = strings.unavailable;
        }
        return;
      }
      idInputs.forEach((inp) => {
        inp.value = variant.id;
        inp.disabled = false;
      });
      if (this.url) {
        try { window.history.replaceState({}, '', `${this.url}?variant=${variant.id}`); } catch (e) { /* ignore */ }
      }
      const sticky = section.querySelector('[data-sticky-variant]');
      if (sticky) sticky.value = variant.id;
      if (variant.featured_media) {
        const gallery = section.querySelector('media-gallery');
        if (gallery) gallery.showMedia(variant.featured_media.id);
      }
      this.renderSection(variant.id, section);
    }
    renderSection(variantId, section) {
      const req = (this.req = `${this.url}?variant=${variantId}&section_id=${this.sectionId}`);
      fetch(req)
        .then((r) => r.text())
        .then((html) => {
          if (req !== this.req) return;
          const doc = parseHTML(html);
          section.querySelectorAll('[data-dynamic-id]').forEach((el) => {
            const fresh = doc.querySelector(`[data-dynamic-id="${el.dataset.dynamicId}"]`);
            if (!fresh) return;
            if (el.tagName === 'QUANTITY-BREAKS') {
              const checked = el.querySelector('input:checked');
              const idx = checked ? Array.from(el.querySelectorAll('input')).indexOf(checked) : -1;
              el.innerHTML = fresh.innerHTML;
              if (idx > -1) {
                const inputs = el.querySelectorAll('input');
                if (inputs[idx]) inputs[idx].checked = true;
              }
            } else if (el.tagName === 'BUTTON') {
              el.innerHTML = fresh.innerHTML;
              if (fresh.hasAttribute('disabled')) el.setAttribute('disabled', '');
              else el.removeAttribute('disabled');
            } else {
              el.innerHTML = fresh.innerHTML;
            }
          });
          const freshId = doc.querySelector('.product-variant-id');
          if (freshId) section.querySelectorAll('.product-variant-id').forEach((i) => (i.disabled = freshId.disabled));
        })
        .catch(() => {});
    }
  }
  customElements.define('variant-selects', VariantSelects);

  /* ---------- media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-gallery-slider]');
      this.items = Array.from(this.querySelectorAll('.media-gallery__item'));
      this.thumbs = Array.from(this.querySelectorAll('.media-gallery__thumb'));
      this.dots = Array.from(this.querySelectorAll('.media-gallery__dots .slider-dot'));
      this.counter = this.querySelector('[data-gallery-current]');
      this.addEventListener('click', (e) => {
        const go = e.target.closest('[data-gallery-goto]');
        if (go) this.goTo(parseInt(go.dataset.galleryGoto, 10));
        if (e.target.closest('[data-gallery-prev]')) this.goTo(this.index() - 1);
        if (e.target.closest('[data-gallery-next]')) this.goTo(this.index() + 1);
        const zoom = e.target.closest('[data-zoom]');
        if (zoom) this.openLightbox(zoom.dataset.zoom);
      });
      if (this.list) {
        this.list.addEventListener('scroll', window.ukl.debounce(() => this.sync(), 50), { passive: true });
      }
      const active = this.items.findIndex((i) => i.classList.contains('is-active'));
      if (active > 0) requestAnimationFrame(() => this.goTo(active, 'auto'));
    }
    index() {
      if (!this.list || !this.items.length) return 0;
      const w = this.items[0].offsetWidth || 1;
      const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
      return Math.round(this.list.scrollLeft / (w + gap));
    }
    isSliderLayout() {
      return this.list && this.list.scrollWidth > this.list.clientWidth + 2;
    }
    goTo(i, behavior = 'smooth') {
      if (!this.items.length) return;
      if (i < 0) i = this.items.length - 1;
      if (i >= this.items.length) i = 0;
      const item = this.items[i];
      if (this.isSliderLayout()) {
        this.list.scrollTo({ left: item.offsetLeft - this.items[0].offsetLeft, behavior });
      } else {
        item.scrollIntoView({ behavior, block: 'nearest' });
      }
      this.setActive(i);
    }
    showMedia(mediaId) {
      const i = this.items.findIndex((el) => el.dataset.mediaId === String(mediaId));
      if (i > -1) this.goTo(i);
    }
    sync() {
      if (this.isSliderLayout()) this.setActive(this.index());
    }
    setActive(i) {
      this.items.forEach((el, idx) => {
        el.classList.toggle('is-active', idx === i);
        if (idx !== i) el.querySelectorAll('video').forEach((v) => v.pause());
      });
      this.thumbs.forEach((t, idx) => t.setAttribute('aria-current', idx === i ? 'true' : 'false'));
      this.dots.forEach((d, idx) => d.setAttribute('aria-current', idx === i ? 'true' : 'false'));
      if (this.counter) this.counter.textContent = String(i + 1);
      const activeThumb = this.thumbs[i];
      if (activeThumb && activeThumb.parentElement && activeThumb.parentElement.parentElement) {
        const list = activeThumb.parentElement.parentElement;
        if (list.scrollHeight > list.clientHeight) list.scrollTo({ top: activeThumb.parentElement.offsetTop - list.offsetTop, behavior: 'smooth' });
      }
    }
    openLightbox(src) {
      const section = this.closest('section');
      const dialog = section && section.querySelector('.media-lightbox');
      if (!dialog || !dialog.showModal) return;
      dialog.querySelector('img').src = src;
      dialog.showModal();
      dialog.querySelector('[data-lightbox-close]').onclick = () => dialog.close();
      dialog.onclick = (e) => {
        if (e.target === dialog) dialog.close();
      };
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------- sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      this.mainBtn = this.form ? this.form.querySelector('[type="submit"]') : null;
      this.btn = this.querySelector('[data-sticky-submit]');
      this.select = this.querySelector('[data-sticky-variant]');
      if (this.dataset.display === 'always') this.classList.add('is-visible');
      else if (this.mainBtn && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([entry]) => {
          const above = entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', !entry.isIntersecting && above);
        });
        this.io.observe(this.mainBtn);
      }
      if (this.btn) this.btn.addEventListener('click', () => this.onClick());
      if (this.select) {
        this.select.addEventListener('change', () => {
          const picker = document.getElementById('VariantSelects-' + this.dataset.section);
          if (picker) picker.selectVariantById(this.select.value);
          else {
            document.querySelectorAll(`#MainProduct-${this.dataset.section} .product-variant-id`).forEach((i) => (i.value = this.select.value));
          }
        });
      }
      this.syncPadding();
    }
    disconnectedCallback() {
      if (this.io) this.io.disconnect();
    }
    syncPadding() {
      document.body.style.setProperty('--sticky-atc-height', this.offsetHeight + 'px');
    }
    onClick() {
      const fn = this.dataset.function || 'add_to_cart';
      if (fn === 'add_to_cart' && this.form) {
        if (this.form.requestSubmit) this.form.requestSubmit();
        else this.form.dispatchEvent(new Event('submit', { cancelable: true }));
        return;
      }
      const target = document.getElementById('ProductInfo-' + this.dataset.section);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
  customElements.define('sticky-atc', StickyAtc);
})();

/* delivery timeline dates */
(function () {
  'use strict';
  class ShippingCheckpoints extends HTMLElement {
    connectedCallback() {
      const days = (this.dataset.days || 'Mon, Tue, Wed, Thu, Fri, Sat, Sun').split(',').map((s) => s.trim());
      const months = (this.dataset.months || 'Jan, Feb, Mar, Apr, May, Jun, Jul, Aug, Sep, Oct, Nov, Dec').split(',').map((s) => s.trim());
      const format = this.dataset.format || 'mm_dd';
      const pad = (n) => String(n).padStart(2, '0');
      const fmt = (d) => {
        const day = days[(d.getDay() + 6) % 7] || '';
        const mon = months[d.getMonth()] || '';
        const dd = d.getDate();
        const mm = pad(d.getMonth() + 1);
        switch (format) {
          case 'day_mm_dd': return `${day}, ${mon} ${dd}`;
          case 'day_dd_mm': return `${day}, ${dd}. ${mon}`;
          case 'dd_mm': return `${dd}. ${mon}`;
          case 'dd_mm_no_dot': return `${dd} ${mon}`;
          case 'day_dd_mm_numeric': return `${day}, ${pad(dd)}.${mm}`;
          case 'dd_mm_numeric': return `${pad(dd)}.${mm}`;
          default: return `${mon} ${dd}`;
        }
      };
      const addDays = (n) => {
        const d = new Date();
        d.setDate(d.getDate() + (parseInt(n, 10) || 0));
        return d;
      };
      this.querySelectorAll('.shipping-checkpoint').forEach((cp) => {
        const start = fmt(addDays(cp.dataset.min));
        const end = fmt(addDays(cp.dataset.max));
        cp.querySelectorAll('[data-template]').forEach((el) => {
          el.innerHTML = el.dataset.template.replace(/\[start_date\]/g, start).replace(/\[end_date\]/g, end);
        });
      });
    }
  }
  customElements.define('shipping-checkpoints', ShippingCheckpoints);
})();

